import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function usePagination(items, pageSize = 10, resetKey) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [resetKey]);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const pageItems = useMemo(
    () => items.slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize]
  );
  return { page, setPage, totalPages, pageItems, total, pageSize };
}

function pageWindow(page, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = new Set([1, totalPages, page, page - 1, page + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

export function Pagination({ page, totalPages, total, pageSize, onChange, testId = "pagination" }) {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const window = pageWindow(page, totalPages);

  return (
    <div className="flex items-center justify-between gap-4 mt-6 flex-wrap" data-testid={testId}>
      <div className="text-xs text-[#5C5652]" data-testid={`${testId}-range`}>
        Showing {from}–{to} of {total}
      </div>
      <div className="flex items-center gap-1">
        <button
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
          data-testid={`${testId}-prev`}
          className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421] hover:text-[#2A2421] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={14} /> Prev
        </button>
        {window.map((p, i) =>
          p === "…" ? (
            <span key={`gap-${i}`} className="px-2 text-[#B8B2AA] text-xs">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              data-testid={`${testId}-page-${p}`}
              className={`min-w-[36px] px-2 py-2 text-xs transition-colors ${
                p === page
                  ? "bg-[#2A2421] text-white border border-[#2A2421]"
                  : "border border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421] hover:text-[#2A2421]"
              }`}
            >
              {p}
            </button>
          )
        )}
        <button
          disabled={page === totalPages}
          onClick={() => onChange(page + 1)}
          data-testid={`${testId}-next`}
          className="inline-flex items-center gap-1 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421] hover:text-[#2A2421] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
